import { useMemo } from "react";

import {
  Select,
  SelectEvent,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { SchoolDataType } from "./types";
import { transformToLanguageObject, transformToSubjectObject } from "./utils";
import { FormCard } from "../../../../../components";
import { SectionHeader } from "../../../../../components/FormControls";

interface IProps {
  onUpdate: (schoolData: SchoolDataType) => void;
  schoolData: SchoolDataType;
}

export const SchoolInfoAccordion = ({ onUpdate, schoolData }: IProps) => {
  const { t } = useTranslation();

  const {
    enrollmentInstitution,
    enrollmentInstitutionTown,
    enrollmentInstitutionMunicipality,
    enrollmentInstitutionCounty,
    instituionSchoolYearMandatoryForeignLanguages,
    institutionSchoolYearElectiveSubjects,
    educationalProgram,
  } = schoolData;

  const institutionId = enrollmentInstitution?.id ?? 0;
  const schoolYearId = enrollmentInstitution?.schoolYear?.id ?? 0;

  const handleProgramChange = (e: React.ChangeEvent<SelectEvent>) => {
    const programId = Number(e.target.value);
    const selectedProgram = enrollmentInstitution?.programs?.find(
      (p) => p.id === programId
    );

    if (!selectedProgram) {
      return onUpdate({
        ...schoolData,
        educationalProgram: null,
      });
    }

    onUpdate({
      ...schoolData,
      educationalProgram: {
        id: selectedProgram.id,
        institution: {
          id: institutionId,
          name: enrollmentInstitution?.name ?? "",
        },
        schoolYearId,
        program: {
          id: selectedProgram.programId,
          name: selectedProgram.name ?? "",
        },
      },
    });
  };

  const handleMandatoryLanguagesChange = (
    e: React.ChangeEvent<SelectEvent>
  ) => {
    const languageIds = Array.isArray(e.target.value)
      ? e.target.value.map(Number)
      : [Number(e.target.value)];

    const selectedLanguages =
      enrollmentInstitution?.mandatoryForeignLanguages?.reduce((acc, lang) => {
        if (languageIds.includes(lang.id)) {
          acc.push(
            transformToLanguageObject(lang, institutionId, schoolYearId)
          );
        }
        return acc;
      }, [] as ReturnType<typeof transformToLanguageObject>[]) ?? [];

    onUpdate({
      ...schoolData,
      instituionSchoolYearMandatoryForeignLanguages: selectedLanguages,
    });
  };

  const handleElectiveSubjectsChange = (e: React.ChangeEvent<SelectEvent>) => {
    const subjectIds = Array.isArray(e.target.value)
      ? e.target.value.map(Number)
      : [Number(e.target.value)];

    const selectedSubjects =
      enrollmentInstitution?.electiveSubjects?.reduce((acc, subject) => {
        if (subjectIds.includes(subject.id)) {
          acc.push(
            transformToSubjectObject(subject, institutionId, schoolYearId)
          );
        }
        return acc;
      }, [] as ReturnType<typeof transformToSubjectObject>[]) ?? [];

    onUpdate({
      ...schoolData,
      institutionSchoolYearElectiveSubjects: selectedSubjects,
    });
  };

  const mandatoryLanguageIds = useMemo(
    () =>
      instituionSchoolYearMandatoryForeignLanguages?.map((lang) => lang.id) ??
      [],
    [instituionSchoolYearMandatoryForeignLanguages]
  );

  const electiveSubjectIds = useMemo(
    () =>
      institutionSchoolYearElectiveSubjects?.map((subject) => subject.id) ?? [],
    [institutionSchoolYearElectiveSubjects]
  );

  return (
    <FormCard className="position-relative">
      <SectionHeader title={t("enrollmentForm.schoolInfo.enrollmentSchool")} />
      <Typography variant="smallHeading" className="mb-3">
        {t("enrollmentForm.schoolInfo.schoolType")}
      </Typography>
      <Row className="mb-2">
        <Col md={3}>
          <Typography variant="small" className="text-muted">
            {t("enrollmentForm.schoolInfo.name")}
          </Typography>
          <Typography variant="base">
            {enrollmentInstitution?.name || ""}
          </Typography>
        </Col>
        <Col md={3}>
          <Typography variant="small" className="text-muted">
            {t("enrollmentForm.schoolInfo.county")}
          </Typography>
          <Typography variant="base">
            {enrollmentInstitutionCounty?.name || ""}
          </Typography>
        </Col>
        <Col md={3}>
          <Typography variant="small" className="text-muted">
            {t("enrollmentForm.schoolInfo.municipality")}
          </Typography>
          <Typography variant="base">
            {enrollmentInstitutionMunicipality?.name || ""}
          </Typography>
        </Col>
        <Col md={3}>
          <Typography variant="small" className="text-muted">
            {t("enrollmentForm.schoolInfo.town")}
          </Typography>
          <Typography variant="base">
            {enrollmentInstitutionTown?.name || ""}
          </Typography>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md={4}>
          <Select
            label={t("enrollmentForm.schoolInfo.program")}
            value={educationalProgram?.id ?? null}
            selectData={{
              keyProp: "id",
              valueProp: "id",
              textProp: "name",
              data: enrollmentInstitution?.programs ?? [],
            }}
            onChange={handleProgramChange}
          />
        </Col>
        <Col md={4}>
          <Select
            label={t("enrollmentForm.schoolInfo.mandatoryForeignLanguages")}
            value={mandatoryLanguageIds}
            multiple
            selectData={{
              keyProp: "id",
              valueProp: "id",
              textProp: "name",
              data: enrollmentInstitution?.mandatoryForeignLanguages ?? [],
            }}
            onChange={handleMandatoryLanguagesChange}
          />
        </Col>
        <Col md={4}>
          <Select
            label={t("enrollmentForm.schoolInfo.electiveSubjects")}
            value={electiveSubjectIds}
            multiple
            selectData={{
              keyProp: "id",
              valueProp: "id",
              textProp: "name",
              data: enrollmentInstitution?.electiveSubjects ?? [],
            }}
            onChange={handleElectiveSubjectsChange}
          />
        </Col>
      </Row>
    </FormCard>
  );
};
